import AdvancedController from "../lib/advanced-controller"
import { gsap } from 'gsap';
import ScrollTrigger from "gsap/ScrollTrigger";
import { debounce } from "../lib/utils";
// import ScrollTrigger from "gsap/ScrollTrigger";

const BASE_ANIMATION_TIME = 1;
const PERSPECTIVE_SCENE_START_TIME = 1.8 * BASE_ANIMATION_TIME
const PERSPECTIVE_MOVE_START_TIME = 2.8 * BASE_ANIMATION_TIME

const KF_SCALE_START = 0.05;
const KF_SCALE_END = 2.2;
const KF_OPACITY_START = 0.1;
const KF_OPACITY_END = 1;
const BLUR_PX = 12;
const EASE_FUNCTION = t => (--t) * t * t + 1
// const EASE_FUNCTION = t => t * (2 - t)
// const EASE_FUNCTION = t => 1 - (--t) * t * t * t

// gsap.registerPlugin(ScrollTrigger);

export default class Scene06Controller extends AdvancedController {

  static targets = [
    "container",
    "image",
    "personWrapper",
    "personImage",
    "personContent",
    "teamCanvas",
    "content",
    "people",
  ];

  static values = {
  }

  connect() {
    super.connect();

    // keep track of the number loaded images for the desktop version
    this.loadedImages = 0
    // keep track of the loading progress, isSceneLoaded = true means loading is kicked off
    this.isSceneLoaded = false

    window.addEventListener('resize-recalculate', this.maybeLoadScene.bind(this))

    if (window.innerWidth < 992) {
      this.initMobileScene();
    } else {
      // kick of the image loading that kicks off the desktop init later
      this.isSceneLoaded = true;
      this.loadImages();
    }

  }

  maybeLoadScene() {
    // ensure the timeline is initialized, no matter was happens, the scene
    // will be initialized after this code again
    this.deinitTimeline();

    if (!this.isSceneLoaded) {
      // first time desktop is needed, the images for the scene are needed
      if (window.innerWidth >= 992) {
        // setting isSceneLoaded to true ensures that loading is not kicked
        // of a second time
        this.isSceneLoaded = true;
        // load the images (and that leads to initDesktopScene later)
        this.loadImages();
      }
    } else {
      // just initialize the scenes
      if (window.innerWidth >= 992) {
        this.initDesktopScene();
      } else {
        this.initMobileScene();
      }
    }
  }

  loadImages() {
    for (const image of this.personImageTargets) {
      let isLoaded = image.complete && image.naturalHeight !== 0;
      if (isLoaded) {
        this.updateLoadCount();
      } else {
        image.addEventListener('load', this.updateLoadCount.bind(this))
      }
    }
  }

  // this counts how many management images are loaded, if all are loaded
  // this triggers the initScene method
  updateLoadCount() {
    this.loadedImages += 1;
    if (this.loadedImages === this.personImageTargets.length) {
      this.initDesktopScene();
    }
  }

  initMobileScene() {
    this.peopleTarget.style.display = "none";
    this.contentTarget.style.opacity = 1;

    this.timeline = gsap.timeline({
      scrollTrigger: {
        start: "-64px top",
        pin: this.containerTarget,
        end: "25%",
        scrub: 0.5,
        id: 'scene06-scroll-trigger',
      }
    })
    this.timeline.to(document.body, { opacity: 1, duration: BASE_ANIMATION_TIME })
  }

  deinitTimeline() {
    if (typeof this.timeline !== 'undefined') {
      const scrollTrigger = ScrollTrigger.getById("scene06-scroll-trigger")
      scrollTrigger.kill(true);
      this.timeline.pause(0).kill(true)
      this.timeline = null;
    }
  }

  // completely initializes the timeline, calculates the image positions, etc.
  initDesktopScene() {

    this.peopleTarget.style.display = "block"
    this.contentTarget.style.opacity = 0;

    this.timeline = gsap.timeline({
      scrollTrigger: {
        start: "-64px top",
        pin: this.containerTarget,
        end: "1600%",
        scrub: 0.5,
        id: 'scene06-scroll-trigger',
      }
    })

    this.timeline
      // First of all, hide the management about text
      .to(this.contentTarget, { opacity: 1, duration: 0.6 * BASE_ANIMATION_TIME, ease: 'none' })
      .to(document.body, { opacity: 1, duration: 0.6 * BASE_ANIMATION_TIME, ease: 'none' })
      .to(this.contentTarget, { opacity: 0, duration: 0.6 * BASE_ANIMATION_TIME, ease: 'none' })

    // calculate the start position of all people
    this.calculateTeamPosition();

    // sample the width and height of the first person
    // this is the reference size for the calculation of the size in 
    // the virtual 3D perspective
    const singleImageWidthSample = this.personWrapperTargets[0].offsetWidth
    const singleImageHeightSample = this.personWrapperTargets[0].offsetHeight

    // define the start and end position, which are the X-values that the right
    // side of the person will be aligned to, i.e. at the start the last person
    // will be aligned to the right of the teamCanvasTarget and the first person
    // will be aligned to 1/3 of the window width.
    const kfXPosStart = window.innerWidth / 3
    const kfXPosEnd = this.teamCanvasTarget.offsetWidth + this.teamCanvasTarget.offsetLeft

    /**
     * This function interpolates a persons size and position, by linearly
     * interpolating from the given start position and scale to the end
     * position and scale. The properties are returned as object that can be
     * used as gsap.to params.
     * @param {*} progress between 0 and 1
     * @returns { left, width, height, filter, ease: 'none' }
     */
    const getInterpolatedSizeProps = (progress) => {
      // calculated the ease progress and the inverted progress
      const easedProgress = EASE_FUNCTION(progress)
      const invertedProgress = 1 - easedProgress;

      // calculate the current scale factor
      const scale = KF_SCALE_START + (KF_SCALE_END - KF_SCALE_START) * invertedProgress;

      // calculate the current opacity
      const opacity = KF_OPACITY_START + (KF_OPACITY_END - KF_OPACITY_START) * invertedProgress;

      // calculate the new width and height using the scale factor
      const newW = singleImageWidthSample * scale
      const newH = singleImageHeightSample * scale

      // calculate the left position
      const newLeft = kfXPosStart + (kfXPosEnd - kfXPosStart) * easedProgress

      return {
        left: `${newLeft - newW}px`, // right aligned, i.e. subtract the width
        width: `${newW}px`,
        height: `${newH}px`,
        filter: `blur(${Math.max(0, easedProgress * BLUR_PX)}px)`, // max 0px, blur cant go below 0
        opacity: opacity,
        // * 0.66 leads to a small break, as the next keyframe is started at 1 (BASE_ANIMATION_TIME)
        duration: BASE_ANIMATION_TIME * 0.66,
        ease: 'none'
      }

    }

    // iterate over all persons to set the animation from the still image
    // to the perspective view
    let i = 0;
    for (const personWrapper of this.personWrapperTargets) {
      // depending on the index of the person, calculate the progress
      // first person = 0; last person = 1
      const progress = i / (this.personWrapperTargets.length - 1);
      // get the interpolated animation properties
      const animProps = getInterpolatedSizeProps(progress)
      // add the animation to the timeline, all at the same delayed time
      // (wait for the text to fade out)
      this.timeline.to(personWrapper, animProps, PERSPECTIVE_SCENE_START_TIME)
      i++;
    }

    this.timeline.fromTo(this.personContentTargets[0], { x: '+=100vw', filter: "blur(40px)", scale: 2, opacity: 0.8 }, { x: "0vw", scale: 1, filter: "blur(0px)", display: "block", opacity: 1 }, PERSPECTIVE_SCENE_START_TIME)

    /**
     * This is the part that animates the movement of all people in the 
     * perspective queue.
     * The concept of the subtimeline is not so simple, thus it will be explained
     * in detail. We think in five virtual keyframes. We start with the first person
     * (Seb) in focus, this is keyframe #1. In the next keyframe #2 the first
     * person is moved to the outside of the viewport, all other people that
     * lined up in the queue will move forward one position. All keyframes
     * with the person in focus:
     * # 0 Seb
     * # 1 Kim
     * # 2 Mar
     * # 3 Axe
     * # 4 Jos
     * For keyframe #1 all people need to move. For keyframe #2 only Kim-Jos
     * need to move. Keyframe #3 moves only Mar-Axe, and so on (the people
     * that are not in the viewport any more don't need to move).
     */
    const subTimeline = gsap.timeline()

    // calculate a position "before" the timeline, which results in the 
    // next virtual keyframe outside the screen
    const lastKfAnimProps = getInterpolatedSizeProps(-0.25)

    // we iterate all people, because (1 person = 1 keyframe)
    // that means we create five keyframes (at the moment)
    for (let k = 0; k < this.personWrapperTargets.length; k++) {
      // this loop iterates the people that need to be moved, each keyframe
      // less people need to be moved (as the ppl. outside the screen don't
      // need to be moved any more)
      for (let m = k; m < this.personWrapperTargets.length; m++) {
        // get the persons DOM node
        const personWrapperTarget = this.personWrapperTargets[m];
        // calc the index of this person, 0..5, the person in focus has always
        // 0, the next 1, and so on
        const positionIndex = m - k
        // the person in focus has 0 as positionIndex, thus we handle the first
        // person differently to the other people
        if (positionIndex === 0) {
          // if this is the first one, it needs to leave the viewport
          // so we use the pre-computed lastKfAnimProps to move the
          // person out of the viewport
          subTimeline.to(personWrapperTarget, lastKfAnimProps, k)
        } else {
          // move the person to the next keyframe
          const progress = (this.personWrapperTargets.length - positionIndex + 1) / this.personWrapperTargets.length
          const animProps = getInterpolatedSizeProps(1 - progress)
          subTimeline.to(personWrapperTarget, animProps, k)
        }
      }
      // reveal the persons text description
      subTimeline.to(this.personContentTargets[k], { display: 'none', opacity: 0, ease: 'none', duration: 0.25 * BASE_ANIMATION_TIME }, k)
      subTimeline.fromTo(this.personContentTargets[k + 1], { x: '+=100vw', filter: "blur(40px)", scale: 2, opacity: 0.8 }, { x: "0vw", scale: 1, filter: "blur(0px)", display: 'block', opacity: 1, ease: 'none', duration: 0.25 * BASE_ANIMATION_TIME }, k + 0.25 * BASE_ANIMATION_TIME)
    }

    // add the subTimeline with a slight delay to the main timeline
    this.timeline.add(subTimeline, PERSPECTIVE_MOVE_START_TIME)
  }

  /**
   * Term definitions:
   * canvas - Referes NOT to a HTML5 canvas, but to the "thought" area where the people
   *          should be displayed, which is defined by this.teamCanvasTarget
   * wrapperImage - The virtual "image" that is the sum of its parts, i.e. all individual images,
   *   it is not a DOM node, we just calculate the size to calculate the scale and
   *   position of all individual images.
   * 
   * Functionality:
   * Starting with the real size of a single person image the real combined image size is calculated,
   * i.e. one image is 1000x2000, thus when overlapping 50% the total resulting image is
   * 3000x2000.
   * Now that we know the size of the (virtual) combined image, we need to fit it in (similar
   * to "contain") the DOM node where it should be displayed, called the canvas (teamCanvasTarget).
   * As we know the aspect ratio of both, the virtual combined image and the canvas,
   * we can recalculate the scale factor and the offset values for both axis and finally
   * position each individual image, so that it looks like the combined image is centered.
   * Note that not the image (img/personImageTarget), but the wrapper (div/personWrapperTarget) is positioned.
   */
  calculateTeamPosition() {

    const [naturalWidth, naturalHeight] = [this.personImageTargets[0].naturalWidth, this.personImageTargets[0].naturalHeight]
    const [wrapperImageWidth, wrapperImageHeight] = [naturalWidth + (this.personImageTargets.length - 1) * 0.5 * naturalWidth, naturalHeight]
    const [canvasWidth, canvasHeight] = [this.teamCanvasTarget.offsetWidth, this.teamCanvasTarget.offsetHeight]

    const [canvasAspect, wrapperImageAspect] = [canvasWidth / canvasHeight, wrapperImageWidth / wrapperImageHeight]

    // helper function to apply CSS position to a target element by index
    const applyCSS = (el, i, offsetX, offsetY, imageWidth, imageHeight) => {
      const pos = {
        top: `${offsetY}px`,
        left: `${offsetX + i * (imageWidth / 2)}px`,
        width: `${imageWidth}px`,
        height: `${imageHeight}px`
      }

      for (const key in pos) {
        el.style[key] = pos[key];
      }

      i++;
    }

    if (canvasAspect > wrapperImageAspect) {
      const scale = canvasHeight / wrapperImageHeight;

      const newWrapperImageWidth = scale * wrapperImageWidth;
      const [singleImageWidth, singleImageHeight] = [naturalWidth * scale, naturalHeight * scale]

      const offsetX = this.teamCanvasTarget.offsetLeft + (canvasWidth - newWrapperImageWidth) / 2
      const offsetY = this.teamCanvasTarget.offsetTop

      let i = 0
      for (const personWrapperTarget of this.personWrapperTargets) {
        applyCSS(personWrapperTarget, i, offsetX, offsetY, singleImageWidth, singleImageHeight)
        i++;
      }
    } else {
      const scale = canvasWidth / wrapperImageWidth;

      const newWrapperImageHeight = scale * wrapperImageHeight;
      const [singleImageWidth, singleImageHeight] = [naturalWidth * scale, naturalHeight * scale]

      const offsetX = this.teamCanvasTarget.offsetLeft;
      const offsetY = this.teamCanvasTarget.offsetTop + (canvasHeight - newWrapperImageHeight) / 2

      let i = 0
      for (const personWrapperTarget of this.personWrapperTargets) {
        applyCSS(personWrapperTarget, i, offsetX, offsetY, singleImageWidth, singleImageHeight)
        i++;
      }
    }
  }

}
